'use client';
import { useMemo } from 'react';
import { cx } from 'styled-system/css';
import { Market, SportMatchModel, StrongTeam } from '@nf/types/odds';
import { useLocale, useTranslations } from 'next-intl';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { ScoreItem, scoreboardService } from '@/services/scoreboard.service';
import { itemsEmptyCheck, stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { useRouter } from '@/navigation';
import { matchSpecialStyles } from '../match-special.style';
import { matchStyles } from '../../match/match.style';
import { useOddsTableMarsContext } from '@/features/odds-table-mars/context';
import { MoreBetTypesButton } from '../../match-bet-types-button';
import { getMatchMoreBetTypeCount } from '@/utils/match-util';
import { sportsbookService } from '@/services/sportsbook.service';
import { matchService } from '@/services/match.service';
import { useCurrentProperties } from '@/hooks/query';

interface MatchSpeciaInfoProps {
	match: SportMatchModel;
	markets: Market[];
	isShowMatch: boolean;
	toggleMatch(): void;
};
export const MatchSpecialInfo = ({ match, markets, isShowMatch, toggleMatch }: MatchSpeciaInfoProps) => {
	const {
		IsLive,
		TeamId1,
		TeamId2,
		Neu,
		BetTypes,
		GameID,
		MaT,
		LeagueId,
		MatchId
	} = match;
	const { eventMode, eventName } = useCurrentProperties();
	const { sport, gameType } = useOddsTableMarsContext();
	const t = useTranslations('General');
	const locale = useLocale();
	const router = useRouter();
	const oddsData = useAtomValue(oddsPageAtom);
	const teamScoreList = useMemo(() => scoreboardService.getAllSportScore(match), [match]);
	const homeTeamName = getHomeTeamName(TeamId1, Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(TeamId2, oddsData?.teamNameMap);
	const strongTeam = matchService.getStrongTeam(BetTypes);
	const [homeScoreList, awayScoreList] = useMemo(() => [
		scoreboardService.getMatchCricketScore(match, 'home'),
		scoreboardService.getMatchCricketScore(match, 'away')
	], [match])
	
	const getFirstScoreText = (scoreList: ScoreItem[]): string => itemsEmptyCheck(scoreList)
		.chain((scoreList: ScoreItem[]) => stringEmptyCheck(scoreList[0]?.text))
		.fold(
			() => '',
			(teamScore: string) => teamScore
		)
	const handleVisitSingleMatch = () => {
		const singleMatchHref = sportsbookService.makeSingleMatchPath({
			gameId: GameID, 
			maT: MaT, 
			leagueId: LeagueId, 
			matchId: MatchId, 
			eventMode: eventMode, 
			eventName: eventName 
		})
		router.push(`${singleMatchHref + (sport === 50 ? `?gametype=${gameType}`:'')}`), { locale };
	}
	const [teamAScore, teamBScore] = matchEquals(sport)
		.on(50, () => [
			getFirstScoreText(homeScoreList),
			getFirstScoreText(awayScoreList)]
		)
		.on(44, () => [
			`${t('lbl_round')}${getFirstScoreText(teamScoreList)}`,
			'']
		)
		.otherwise(() => getFirstScoreText(teamScoreList).split('-'))

	return (
		<div className={matchSpecialStyles.teamInfo} data-open={isShowMatch}> 
			<div className={matchSpecialStyles.teams} onClick={toggleMatch}>
				<div className={matchSpecialStyles.team}>
					<div className={cx(matchSpecialStyles.teamName, strongTeam === StrongTeam.Home && matchStyles.teamStrong)}>{homeTeamName}</div>
					{
						MaybeMatch(IsLive)
							.chain(() => stringEmptyCheck(teamAScore))
							.fold(
								() => {},
								() => {
									if (sport === 50) {
										return homeScoreList.map((item, index) => (
											<div className={item.isPlay ? matchSpecialStyles.score : matchSpecialStyles.scoreDisabled} key={index}>
												{item.text}
											</div>
										))
									}
									return <div className={matchSpecialStyles.scoreDisabled}>{teamAScore}</div>
								}
							)
					}
				</div>
				<div className={matchSpecialStyles.team}>
					<div className={cx(matchSpecialStyles.teamName, strongTeam === StrongTeam.Away && matchStyles.teamStrong)}>{awayTeamName}</div>
					{
						MaybeMatch(IsLive)
							.chain(() => stringEmptyCheck(teamBScore))
							.fold(
								() => {},
								() => {
									if (sport === 50) {
										return awayScoreList.map((item, index) => (
											<div className={item.isPlay ? matchSpecialStyles.score : matchSpecialStyles.scoreDisabled} key={index}>
												{item.text}
											</div>
										))
									}
									return <div className={matchSpecialStyles.scoreDisabled}>{teamBScore}</div>
								}
							)
					}
				</div>
			</div>
			{
				isShowMatch && (
					<div className={cx(matchStyles.iconButtons, matchSpecialStyles.iconButtons)}>
						<MoreBetTypesButton moreCount={getMatchMoreBetTypeCount(match, markets, gameType)} clickHandler={handleVisitSingleMatch} />
					</div>
				)
			}
			
		</div>
	)
}
